import { axios } from "@/utils/axios-wrapper"
import { getGeneralNextPage } from "@/utils/scroll-context"
import { param } from "drizzle-orm"
import { AdExamplesSearchedResponse } from "models/discover"
import {
	AdExamplesType,
	ApplyFiltersAdExamples,
	AspectAdExamples,
	FilterStateAdExamples,
} from "pages/ad-examples"
import { WizardGalleryHomeResponse } from "pages/api/v1/wizard/gallery/home"
import {
	createContext,
	Dispatch,
	ReactNode,
	useContext,
	useReducer,
} from "react"
import {
	QueryFunction,
	useInfiniteQuery,
	UseInfiniteQueryResult,
} from "react-query"
import { Pages } from "sections/gallery/all-gallery"

type FilterStateActionAdExamples =
	| {
			type: "set_search_query"
			searchQuery: string | null
	  }
	| {
			type: "set_aspect"
			aspect: AspectAdExamples
	  }
	| {
			type: "apply_filters"
	  }
	| {
			type: "clear_all"
	  }

type AdExamplesViewState = {
	dispatch: Dispatch<FilterStateActionAdExamples>
	state: FilterStateAdExamples
	query: UseInfiniteQueryResult<
		SinglePageAdExamples,
		unknown
	>
}

const AdExamplesViewContext =
	createContext<AdExamplesViewState | null>(null)

export function useAdExamplesViewContext(): AdExamplesViewState {
	const result = useContext(AdExamplesViewContext)
	if (!result) {
		throw new Error("You forgot the context")
	}

	return result
}

export interface AdExamplesViewProviderProps {
	children: ReactNode
	initialFilters: FilterStateAdExamples
	initialData?: AdExamplesType
	page: Pages
	pageNumber: number
}

export function deepCompare<T extends object>(
	a: T,
	b: T,
): boolean {
	for (const key in a) {
		if (a[key] !== b[key]) {
			return false
		}
	}

	return true
}

export const TEMPLATES_ITEMS_IN_PAGE = 20

export function AdExamplesViewProvider(
	props: AdExamplesViewProviderProps,
) {
	const {
		initialFilters,
		initialData,
		children,
		page,
		pageNumber,
	} = props

	const [state, dispatch] = useReducer(
		createReducerWithInitialFiltersAdExamples(
			initialFilters,
		),
		initialFilters,
	)

	const query = useInfiniteQuery({
		queryKey: ["ad-examples", state.applyFilters],
		queryFn: getAdExamples(state),
		getNextPageParam: (last) => last.next,
		initialData: () => {
			if (!initialData) {
				return undefined
			}

			if (!deepCompare(initialFilters, state)) {
				return undefined
			}

			let next = pageNumber * TEMPLATES_ITEMS_IN_PAGE

			if (initialData.type !== "searched") {
				next = 3 * pageNumber
			}

			return {
				pages: [
					{
						data: initialData,
						next,
					},
				],
				pageParams: [0],
			}
		},
		staleTime: Infinity,
		cacheTime: 0,
		enabled: page === "ad-examples",
	})

	return (
		<AdExamplesViewContext.Provider
			value={{
				state,
				dispatch,
				query,
			}}>
			{children}
		</AdExamplesViewContext.Provider>
	)
}

function createReducerWithInitialFiltersAdExamples(
	initialFilters: FilterStateAdExamples,
) {
	return function adExamplesViewReducer(
		state: FilterStateAdExamples,
		action: FilterStateActionAdExamples,
	): FilterStateAdExamples {
		switch (action.type) {
			case "set_search_query": {
				return {
					...state,
					searchQuery: action.searchQuery,
				}
			}
			case "set_aspect": {
				return {
					...state,
					aspect: action.aspect,
				}
			}
			case "apply_filters": {
				const { applyFilters: _, ...currentState } = state
				return {
					...state,
					applyFilters: currentState,
				}
			}
			case "clear_all": {
				return {
					...initialFilters,
					searchQuery: state.searchQuery,
				}
			}
		}
	}
}

export interface SinglePageAdExamples {
	data: AdExamplesType
	next: number | undefined
}

export const getAdExamples =
	(
		state: ApplyFiltersAdExamples,
	): QueryFunction<SinglePageAdExamples> =>
	async ({ pageParam }) => {
		const offset = pageParam ?? 0

		if (
			state.searchQuery === null &&
			state.aspect === null
		) {
			const response = await axios
				.get<WizardGalleryHomeResponse>("/api/v1/wizard/gallery/home",
					{
						params: {
							limit: 3,
							offset,
						},
					},
				
				)
				.then((res) => {
					return res.data.sections
				})

			return {
				data: { type: "home", content: response },
				next: getGeneralNextPage(
					response.length,
					pageParam,
				),
			}
		} else {
			const response = await axios
				.post<AdExamplesSearchedResponse>(
					"/api/v1/wizard/gallery/search",
					{
						offset,
						limit: TEMPLATES_ITEMS_IN_PAGE,
						search_query: state.searchQuery ?? "",
						aspect: state.aspect,
					},
				)
				.then((res) => {
					return res.data
				})

			return {
				data: {
					type: "searched",
					content: response.results,
					total: response.total,
				},
				next: getGeneralNextPage(
					response.results.length,
					pageParam,
				),
			}
		}
	}

export function arraysAreEqual<T>(arr1: T[], arr2: T[]) {
	if (arr1.length !== arr2.length) {
		return false
	}
	for (let i = 0; i < arr1.length; i++) {
		if (arr1[i] !== arr2[i]) {
			return false
		}
	}
	return true
}
